<template>
  <div class="api-docs-portal">
    <!-- Top Navigation Bar -->
    <header class="api-header">
      <div class="header-left">
        <router-link to="/" class="back-home-btn">
          <i class="fas fa-arrow-left"></i>
        </router-link>
        <div class="brand">
          <img src="@/assets/images/logo_vn_guide.png" class="brand-logo" alt="Logo" @error="handleLogoError" />
          <div class="brand-text">
            <h1 class="brand-title">{{ collectionName }}</h1>
            <p class="brand-subtitle">Cổng thông tin & Thử nghiệm API</p>
          </div>
        </div>
      </div>

      <!-- Tab Controllers -->
      <div class="tab-controllers">
        <button
          :class="['tab-btn', { active: activeTab === 'docs' }]"
          @click="activeTab = 'docs'"
        >
          <i class="fas fa-book-open"></i> Tài liệu API
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'sandbox' }]"
          @click="activeTab = 'sandbox'"
        >
          <i class="fas fa-terminal"></i> Live Sandbox
        </button>
      </div>
    </header>

    <div class="portal-body">
      <!-- Sidebar Navigation -->
      <aside class="docs-sidebar">
        <div class="search-box">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Tìm kiếm API..."
            class="search-input"
          />
        </div>

        <nav class="sidebar-nav">
          <div
            :class="['nav-item overview-nav', { active: !selectedRequest }]"
            @click="selectOverview"
          >
            <i class="fas fa-info-circle"></i>
            <span>Tổng quan</span>
          </div>

          <!-- Grouped Folders -->
          <div
            v-for="folder in filteredGroupedRequests"
            :key="folder.path"
            class="folder-group"
          >
            <div class="folder-header" @click="toggleFolder(folder.path)">
              <i :class="['fas', isExpanded(folder.path) ? 'fa-chevron-down' : 'fa-chevron-right', 'arrow-icon']"></i>
              <i class="fas fa-folder folder-icon"></i>
              <span class="folder-title">{{ folder.name }}</span>
              <span class="badge count-badge">{{ folder.requests.length }}</span>
            </div>

            <ul v-show="isExpanded(folder.path)" class="folder-requests">
              <li
                v-for="req in folder.requests"
                :key="req.id"
                :class="['request-item', { active: selectedRequest && selectedRequest.id === req.id }]"
                @click="selectRequest(req)"
              >
                <span :class="['method-badge', req.request.method.toLowerCase()]">
                  {{ req.request.method }}
                </span>
                <span class="request-name" :title="req.name">{{ req.name }}</span>
              </li>
            </ul>
          </div>
        </nav>
      </aside>

      <!-- Main Content Area -->
      <main class="docs-content">
        <!-- 1. OVERVIEW DETAIL -->
        <div v-if="!selectedRequest" class="detail-section overview-detail">
          <h2 class="detail-title">Tổng quan API</h2>
          <p class="section-desc">
            Cổng tài liệu cung cấp danh sách đầy đủ các Endpoint dịch vụ, cấu trúc dữ liệu gửi lên (Request) và dữ liệu phản hồi (Response) của hệ thống.
          </p>

          <div class="meta-grid">
            <div class="meta-card">
              <span class="meta-label">Tổng số Endpoint</span>
              <span class="meta-value">{{ allRequests.length }} APIs</span>
            </div>
            <span class="meta-card">
              <span class="meta-label">Môi trường Staging</span>
              <span class="meta-value code-text">https://admin.vntravelguideapp.com/api</span>
            </span>
            <div class="meta-card">
              <span class="meta-label">Môi trường Local</span>
              <span class="meta-value code-text">http://127.0.0.1:5000</span>
            </div>
          </div>

          <div class="alert-box alert-info">
            <i class="fas fa-info-circle alert-icon"></i>
            <div class="alert-content">
              <strong>Mẹo sử dụng:</strong> Bạn có thể chọn bất kỳ API nào từ menu bên trái, xem định dạng tham số và trực tiếp thử nghiệm cuộc gọi API thực tế qua tab <strong>Live Sandbox</strong>.
            </div>
          </div>

          <div class="quick-start-section">
            <h3>Danh sách danh mục API chính</h3>
            <div class="categories-list">
              <div
                v-for="folder in groupedRequests"
                :key="folder.path"
                class="category-card"
                @click="toggleFolder(folder.path); selectRequest(folder.requests[0])"
              >
                <div class="card-icon"><i class="fas fa-folder"></i></div>
                <div class="card-info">
                  <h4>{{ folder.name }}</h4>
                  <p>{{ folder.requests.length }} endpoints khác nhau</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. REQUEST DETAIL -->
        <div v-else class="detail-section request-detail">
          <div class="detail-header-panel">
            <div class="folder-path-breadcrumb">
              {{ selectedRequest.folderName }}
            </div>
            <div class="request-endpoint-row">
              <span :class="['method-badge-large', selectedRequest.request.method.toLowerCase()]">
                {{ selectedRequest.request.method }}
              </span>
              <span class="request-url-text">{{ selectedRequest.request.url }}</span>
              <button class="try-btn" @click="tryInSandbox(selectedRequest)">
                <i class="fas fa-terminal"></i> Thử nghiệm
              </button>
            </div>
            <h2 class="request-display-name">{{ selectedRequest.name }}</h2>
          </div>

          <div v-if="selectedRequest.request.description" class="request-description" v-html="renderDescription(selectedRequest.request.description)">
          </div>

          <!-- Tabs for Details -->
          <div class="detail-tabs">
            <button
              :class="['detail-tab-btn', { active: activeDetailTab === 'params' }]"
              @click="activeDetailTab = 'params'"
            >
              Tham số & Headers
            </button>
            <button
              v-if="hasBody(selectedRequest)"
              :class="['detail-tab-btn', { active: activeDetailTab === 'body' }]"
              @click="activeDetailTab = 'body'"
            >
              Yêu cầu (Body)
            </button>
            <button
              v-if="selectedRequest.response && selectedRequest.response.length"
              :class="['detail-tab-btn', { active: activeDetailTab === 'response' }]"
              @click="activeDetailTab = 'response'"
            >
              Phản hồi Mẫu ({{ selectedRequest.response.length }})
            </button>
          </div>

          <!-- Details Tab Contents -->
          <div class="detail-tab-content">
            <!-- Params and Headers -->
            <div v-show="activeDetailTab === 'params'" class="params-tab">
              <!-- Headers Section -->
              <h3 class="subsection-title">Headers</h3>
              <div v-if="selectedRequest.request.header && selectedRequest.request.header.length" class="table-container">
                <table class="api-table">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Giá trị mặc định/Ví dụ</th>
                      <th>Mô tả</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="header in selectedRequest.request.header" :key="header.key">
                      <td><code>{{ header.key }}</code></td>
                      <td><code>{{ header.value }}</code></td>
                      <td>{{ header.description || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="empty-text">Không yêu cầu headers đặc biệt.</p>

              <!-- Query Params Section -->
              <h3 class="subsection-title mt-4">Query Parameters (URL)</h3>
              <div v-if="selectedRequest.request.query && selectedRequest.request.query.length" class="table-container">
                <table class="api-table">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Giá trị mẫu</th>
                      <th>Mô tả</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="query in selectedRequest.request.query" :key="query.key">
                      <td><code>{{ query.key }}</code></td>
                      <td><code>{{ query.value }}</code></td>
                      <td>{{ query.description || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="empty-text">Không có tham số query parameters.</p>
            </div>

            <!-- Body Tab -->
            <div v-show="activeDetailTab === 'body'" class="body-tab">
              <div class="body-mode-info">
                Chế độ gửi: <code>{{ selectedRequest.request.body.mode }}</code>
              </div>
              <div class="code-block-wrapper">
                <div class="code-block-header">
                  <span class="code-lang">Raw Request Body</span>
                  <button class="copy-btn" @click="copyText(selectedRequest.request.body.raw)">
                    <i class="fas fa-copy"></i> Sao chép
                  </button>
                </div>
                <pre class="code-pre"><code v-html="highlightJson(selectedRequest.request.body.raw)"></code></pre>
              </div>
            </div>

            <!-- Response Tab -->
            <div v-show="activeDetailTab === 'response'" class="response-tab">
              <div v-for="(resp, idx) in selectedRequest.response" :key="idx" class="response-sample-item">
                <div class="response-sample-header">
                  <span class="sample-name">{{ resp.name || 'Sample Response' }}</span>
                  <span :class="['status-badge', resp.code >= 400 ? 'status-error' : 'status-ok']">
                    {{ resp.code }} {{ resp.status }}
                  </span>
                </div>
                <div class="code-block-wrapper">
                  <div class="code-block-header">
                    <span class="code-lang">JSON</span>
                    <button class="copy-btn" @click="copyText(resp.body)">
                      <i class="fas fa-copy"></i> Sao chép
                    </button>
                  </div>
                  <pre class="code-pre"><code v-html="highlightJson(resp.body)"></code></pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <!-- Sandbox Panel (Right Side, visible when activeTab is sandbox or when try mode is triggered) -->
      <aside v-show="activeTab === 'sandbox'" class="sandbox-panel">
        <div class="sandbox-header">
          <h3><i class="fas fa-terminal"></i> API Sandbox</h3>
          <button class="reset-btn" @click="resetSandbox">
            <i class="fas fa-undo"></i> Khôi phục
          </button>
        </div>

        <div class="sandbox-fields">
          <div class="sandbox-field-group">
            <label class="sandbox-label">HTTP Method & URL</label>
            <div class="url-input-row">
              <select v-model="sandboxMethod" class="sandbox-select">
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
              <input v-model="sandboxUrl" type="text" class="sandbox-input url-input" placeholder="http://..." />
            </div>
          </div>

          <div class="sandbox-field-group">
            <label class="sandbox-label">Authorization Token (Bearer)</label>
            <input v-model="sandboxToken" type="text" class="sandbox-input" placeholder="Nhập token xác thực..." />
          </div>

          <div class="sandbox-field-group">
            <label class="sandbox-label">Headers (JSON)</label>
            <textarea v-model="sandboxHeaders" class="sandbox-textarea code-font" placeholder="{ &quot;Device-Id&quot;: &quot;1&quot; }"></textarea>
          </div>

          <div v-if="sandboxMethod !== 'GET'" class="sandbox-field-group">
            <label class="sandbox-label">Request Body (JSON)</label>
            <textarea v-model="sandboxBody" class="sandbox-textarea body-textarea code-font" placeholder="{}"></textarea>
          </div>

          <button
            class="send-request-btn"
            :disabled="sendingRequest"
            @click="sendSandboxRequest"
          >
            <span v-if="sendingRequest"><i class="fas fa-spinner fa-spin"></i> Đang gửi...</span>
            <span v-else><i class="fas fa-paper-plane"></i> Gửi Yêu Cầu</span>
          </button>
        </div>

        <!-- Sandbox Output -->
        <div class="sandbox-output-section">
          <div class="output-header">
            <h4>Phản Hồi Thực Tế</h4>
            <span v-if="responseStatus" :class="['status-badge', responseStatus >= 400 ? 'status-error' : 'status-ok']">
              {{ responseStatus }}
            </span>
          </div>

          <div class="output-screen">
            <div v-if="sendingRequest" class="loading-screen">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
              <p>Đang kết nối tới server...</p>
            </div>
            <div v-else-if="!responseReceived" class="placeholder-screen">
              Nhấp nút "Gửi Yêu Cầu" để gọi API thực tế.
            </div>
            <div v-else class="response-display">
              <div class="response-meta">
                <span>Thời gian: {{ responseTime }}ms</span>
                <span>Kích thước: {{ responseSize }} bytes</span>
              </div>
              <pre class="code-pre"><code v-html="highlightJson(responseBody)"></code></pre>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import collectionData from './collection.json'

export default {
  name: 'ApiDocsPublic',
  data() {
    return {
      activeTab: 'docs', // 'docs' or 'sandbox'
      activeDetailTab: 'params', // 'params', 'body', 'response'
      searchQuery: '',
      selectedRequest: null,
      expandedFolders: [],
      logoError: false,

      // Collection variables
      collectionName: '',
      allRequests: [],
      groupedRequests: [],

      // Sandbox variables
      sandboxMethod: 'GET',
      sandboxUrl: '',
      sandboxToken: '',
      sandboxHeaders: '{}',
      sandboxBody: '{}',
      sendingRequest: false,
      responseReceived: false,
      responseStatus: null,
      responseTime: 0,
      responseSize: 0,
      responseBody: ''
    }
  },
  computed: {
    filteredGroupedRequests() {
      if (!this.searchQuery) return this.groupedRequests
      const query = this.searchQuery.toLowerCase()

      return this.groupedRequests.map(folder => {
        const matchingRequests = folder.requests.filter(req =>
          req.name.toLowerCase().includes(query) ||
          req.request.url.toLowerCase().includes(query)
        )
        return {
          ...folder,
          requests: matchingRequests
        }
      }).filter(folder => folder.requests.length > 0)
    }
  },
  watch: {
    selectedRequest(newVal) {
      if (newVal) {
        if (newVal.response && newVal.response.length) {
          this.activeDetailTab = 'params'
        } else if (this.hasBody(newVal)) {
          this.activeDetailTab = 'body'
        } else {
          this.activeDetailTab = 'params'
        }
      }
    }
  },
  created() {
    this.parseCollection()
    // Select overview by default
    this.selectedRequest = null
  },
  methods: {
    parseCollection() {
      this.collectionName = collectionData.info.name || 'Museum API'
      const requests = []
      const groups = {}

      const traverse = (items, parentPath = []) => {
        items.forEach((item, index) => {
          if (item.item) {
            traverse(item.item, [...parentPath, item.name])
          } else {
            const reqId = `${parentPath.join('_')}_${item.name}_${index}`
            const requestObj = {
              id: reqId,
              name: item.name,
              request: item.request,
              response: item.response || [],
              folderName: parentPath.join(' / '),
              folderPath: parentPath
            }
            requests.push(requestObj)

            // Group by primary folder path
            const folderPathKey = parentPath.join(' / ') || 'General'
            if (!groups[folderPathKey]) {
              groups[folderPathKey] = {
                name: parentPath[parentPath.length - 1] || 'General',
                path: folderPathKey,
                requests: []
              }
            }
            groups[folderPathKey].requests.push(requestObj)
          }
        })
      }

      traverse(collectionData.item)

      this.allRequests = requests
      this.groupedRequests = Object.values(groups)

      // Auto expand the first few folders
      this.expandedFolders = this.groupedRequests.slice(0, 3).map(g => g.path)
    },
    toggleFolder(path) {
      const idx = this.expandedFolders.indexOf(path)
      if (idx > -1) {
        this.expandedFolders.splice(idx, 1)
      } else {
        this.expandedFolders.push(path)
      }
    },
    isExpanded(path) {
      return this.expandedFolders.includes(path)
    },
    selectRequest(req) {
      this.selectedRequest = req
    },
    selectOverview() {
      this.selectedRequest = null
    },
    handleLogoError() {
      this.logoError = true
    },
    hasBody(req) {
      return req && req.request && req.request.body && req.request.body.raw
    },
    renderDescription(desc) {
      if (!desc) return ''
      // Simple markdown parser for headers/bold/lists
      return desc
        .replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/g, '<h4>$1</h4>') // convert high headers to h4
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
    },
    tryInSandbox(req) {
      this.sandboxMethod = req.request.method
      this.sandboxUrl = req.request.url

      // Prepopulate body
      if (req.request.body && req.request.body.raw) {
        this.sandboxBody = req.request.body.raw
      } else {
        this.sandboxBody = '{}'
      }

      // Prepopulate headers
      const headersObj = {}
      if (req.request.header) {
        req.request.header.forEach(h => {
          if (h.key.toLowerCase() !== 'authorization') {
            headersObj[h.key] = h.value
          } else {
            // strip bearer prefix if present
            if (h.value.toLowerCase().startsWith('bearer ')) {
              this.sandboxToken = h.value.substring(7)
            } else {
              this.sandboxToken = h.value
            }
          }
        })
      }
      this.sandboxHeaders = JSON.stringify(headersObj, null, 2)

      // Open sandbox panel
      this.activeTab = 'sandbox'
    },
    resetSandbox() {
      this.sandboxMethod = 'GET'
      this.sandboxUrl = ''
      this.sandboxToken = ''
      this.sandboxHeaders = '{}'
      this.sandboxBody = '{}'
      this.responseReceived = false
      this.responseStatus = null
    },
    sendSandboxRequest() {
      this.sendingRequest = true
      this.responseReceived = false

      let headers = {}
      try {
        headers = JSON.parse(this.sandboxHeaders)
      } catch (e) {
        this.$message.error('Headers JSON không hợp lệ')
        this.sendingRequest = false
        return
      }

      if (this.sandboxToken) {
        headers['Authorization'] = `Bearer ${this.sandboxToken}`
      }

      let data = null
      if (this.sandboxMethod !== 'GET') {
        try {
          data = JSON.parse(this.sandboxBody)
        } catch (e) {
          this.$message.error('Request Body JSON không hợp lệ')
          this.sendingRequest = false
          return
        }
      }

      const startTime = Date.now()

      axios({
        method: this.sandboxMethod,
        url: this.sandboxUrl,
        headers,
        data
      })
        .then(response => {
          this.responseTime = Date.now() - startTime
          this.responseStatus = response.status
          this.responseBody = JSON.stringify(response.data, null, 2)
          this.responseSize = this.responseBody.length
          this.responseReceived = true
        })
        .catch(err => {
          this.responseTime = Date.now() - startTime
          this.responseStatus = err.response ? err.response.status : 'ERROR'
          const errData = err.response ? err.response.data : { error: err.message }
          this.responseBody = JSON.stringify(errData, null, 2)
          this.responseSize = this.responseBody.length
          this.responseReceived = true
        })
        .finally(() => {
          this.sendingRequest = false
        })
    },
    // Highlights JSON Syntax
    highlightJson(json) {
      if (!json) return ''
      if (typeof json !== 'string') {
        json = JSON.stringify(json, undefined, 2)
      }
      json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

      return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, function (match) {
        let cls = 'number'
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'key'
          } else {
            cls = 'string'
          }
        } else if (/true|false/.test(match)) {
          cls = 'boolean'
        } else if (/null/.test(match)) {
          cls = 'null'
        }
        return '<span class="hl-' + cls + '">' + match + '</span>'
      })
    },
    copyText(text) {
      if (!text) return
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success('Sao chép thành công!')
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;500&family=Inter:wght@300;400;500;600;700;800&display=swap');

// Style variables
$bg-main: #0b0f19;
$bg-card: #161b26;
$bg-input: #1f2937;
$border-color: #262f40;
$text-main: #f3f4f6;
$text-muted: #9ca3af;
$primary: #10b981;
$secondary: #3b82f6;
$warning: #f59e0b;
$danger: #ef4444;

.api-docs-portal {
  font-family: 'Inter', sans-serif;
  background-color: $bg-main;
  color: $text-main;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }

  // Header Styles
  .api-header {
    background-color: rgba($bg-card, 0.95);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid $border-color;
    padding: 12px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .back-home-btn {
      color: $text-muted;
      font-size: 18px;
      padding: 8px;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.08);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;

      &:hover {
        color: $text-main;
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.2);
        transform: translateX(-2px);
      }
    }

    .brand {
      display: flex;
      align-items: center;
      gap: 12px;

      .brand-logo {
        height: 38px;
        object-fit: contain;
      }

      .brand-text {
        display: flex;
        flex-direction: column;
      }

      .brand-title {
        font-size: 18px;
        font-weight: 700;
        margin: 0;
        letter-spacing: -0.5px;
        background: linear-gradient(135deg, #10b981 0%, #3b82f6 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .brand-subtitle {
        font-size: 11px;
        color: $text-muted;
        margin: 2px 0 0 0;
      }
    }

    .tab-controllers {
      display: flex;
      background: rgba(0, 0, 0, 0.2);
      padding: 4px;
      border-radius: 8px;
      border: 1px solid $border-color;
      gap: 4px;

      .tab-btn {
        background: transparent;
        border: none;
        color: $text-muted;
        font-size: 13px;
        font-weight: 600;
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.2s;

        &:hover {
          color: $text-main;
          background: rgba(255, 255, 255, 0.03);
        }

        &.active {
          color: #ffffff;
          background: $secondary;
          box-shadow: 0 2px 8px rgba($secondary, 0.4);
        }
      }
    }
  }

  // Layout Structure
  .portal-body {
    flex: 1;
    display: flex;
    overflow: hidden;
    height: calc(100vh - 65px);
  }

  // Sidebar Styles
  .docs-sidebar {
    width: 320px;
    border-right: 1px solid $border-color;
    background-color: rgba($bg-card, 0.4);
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    flex-shrink: 0;

    .search-box {
      position: relative;

      .search-icon {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        color: $text-muted;
        font-size: 14px;
      }

      .search-input {
        width: 100%;
        background: $bg-input;
        border: 1px solid $border-color;
        color: $text-main;
        padding: 10px 12px 10px 36px;
        border-radius: 8px;
        font-size: 13px;
        transition: all 0.2s;

        &:focus {
          outline: none;
          border-color: $secondary;
          box-shadow: 0 0 0 2px rgba($secondary, 0.2);
        }
      }
    }

    .sidebar-nav {
      display: flex;
      flex-direction: column;
      gap: 10px;

      .nav-item {
        padding: 10px 14px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: $text-muted;
        display: flex;
        align-items: center;
        gap: 12px;
        transition: all 0.2s;

        &:hover {
          color: $text-main;
          background: rgba(255, 255, 255, 0.03);
        }

        &.active {
          color: #ffffff;
          background: rgba($secondary, 0.12);
          border-left: 3px solid $secondary;
          padding-left: 11px;
        }
      }

      .folder-group {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .folder-header {
          padding: 8px 12px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          color: $text-main;
          font-weight: 600;
          font-size: 13px;
          transition: background 0.2s;

          &:hover {
            background: rgba(255, 255, 255, 0.03);
          }

          .arrow-icon {
            font-size: 10px;
            width: 12px;
            color: $text-muted;
          }

          .folder-icon {
            color: #f1c40f;
          }

          .folder-title {
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .count-badge {
            background: $bg-input;
            color: $text-muted;
            font-size: 10px;
            padding: 2px 6px;
          }
        }

        .folder-requests {
          list-style: none;
          padding: 0 0 0 16px;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
          border-left: 1px dashed rgba(255, 255, 255, 0.07);
          margin-left: 17px;

          .request-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 12px;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.2s;

            &:hover {
              background: rgba(255, 255, 255, 0.03);
            }

            &.active {
              background: rgba($secondary, 0.1);
              color: $secondary;
              font-weight: 600;
            }

            .request-name {
              font-size: 12px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              flex: 1;
            }
          }
        }
      }
    }
  }

  // Method Badges
  .method-badge {
    font-size: 8px;
    font-weight: 800;
    padding: 2px 6px;
    border-radius: 4px;
    text-transform: uppercase;
    width: 48px;
    text-align: center;
    flex-shrink: 0;

    &.get { background: rgba($secondary, 0.15); color: $secondary; }
    &.post { background: rgba($primary, 0.15); color: $primary; }
    &.put { background: rgba($warning, 0.15); color: $warning; }
    &.patch { background: rgba(#9b59b6, 0.15); color: #9b59b6; }
    &.delete { background: rgba($danger, 0.15); color: $danger; }
  }

  .method-badge-large {
    font-size: 11px;
    font-weight: 800;
    padding: 6px 12px;
    border-radius: 6px;
    text-transform: uppercase;
    display: inline-block;

    &.get { background: rgba($secondary, 0.15); color: $secondary; border: 1px solid rgba($secondary, 0.3); }
    &.post { background: rgba($primary, 0.15); color: $primary; border: 1px solid rgba($primary, 0.3); }
    &.put { background: rgba($warning, 0.15); color: $warning; border: 1px solid rgba($warning, 0.3); }
    &.patch { background: rgba(#9b59b6, 0.15); color: #9b59b6; border: 1px solid rgba(#9b59b6, 0.3); }
    &.delete { background: rgba($danger, 0.15); color: $danger; border: 1px solid rgba($danger, 0.3); }
  }

  // Main Content Styles
  .docs-content {
    flex: 1;
    padding: 30px 40px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  .detail-title {
    font-size: 28px;
    font-weight: 800;
    margin-top: 0;
    margin-bottom: 8px;
    letter-spacing: -0.5px;
  }

  .section-desc {
    color: $text-muted;
    font-size: 15px;
    line-height: 1.6;
    margin-bottom: 24px;
  }

  // Meta Grid
  .meta-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 24px;

    .meta-card {
      background: $bg-card;
      border: 1px solid $border-color;
      border-radius: 8px;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 6px;

      .meta-label {
        font-size: 11px;
        text-transform: uppercase;
        color: $text-muted;
        font-weight: 600;
      }

      .meta-value {
        font-size: 14px;
        font-weight: 700;

        &.code-text {
          font-family: 'Fira Code', monospace;
          color: $primary;
          word-break: break-all;
        }
      }
    }
  }

  // Alert Box
  .alert-box {
    background: rgba($bg-card, 0.5);
    border-left: 4px solid $secondary;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    gap: 14px;
    margin-bottom: 30px;
    font-size: 14px;
    line-height: 1.5;

    .alert-icon {
      font-size: 18px;
      color: $secondary;
      margin-top: 2px;
    }
  }

  // Categories List
  .categories-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 16px;
    margin-top: 16px;

    .category-card {
      background: $bg-card;
      border: 1px solid $border-color;
      border-radius: 10px;
      padding: 16px;
      display: flex;
      align-items: center;
      gap: 14px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: $secondary;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .card-icon {
        font-size: 20px;
        color: #f1c40f;
      }

      h4 {
        margin: 0;
        font-size: 14px;
        font-weight: 700;
      }

      p {
        margin: 4px 0 0 0;
        font-size: 11px;
        color: $text-muted;
      }
    }
  }

  // Request Details Header
  .detail-header-panel {
    border-bottom: 1px solid $border-color;
    padding-bottom: 20px;
    margin-bottom: 20px;

    .folder-path-breadcrumb {
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 700;
      color: $text-muted;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
    }

    .request-endpoint-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;

      .request-url-text {
        font-family: 'Fira Code', monospace;
        font-size: 14px;
        color: $text-main;
        flex: 1;
        background: rgba(0, 0, 0, 0.2);
        padding: 6px 12px;
        border-radius: 6px;
        border: 1px solid $border-color;
        word-break: break-all;
      }

      .try-btn {
        background: $primary;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s;

        &:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }
      }
    }

    .request-display-name {
      font-size: 22px;
      font-weight: 800;
      margin: 0;
    }
  }

  .request-description {
    background: rgba($bg-card, 0.3);
    border: 1px solid $border-color;
    padding: 16px;
    border-radius: 8px;
    color: $text-muted;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 24px;

    h4 {
      color: white;
      margin-top: 14px;
      margin-bottom: 6px;
    }
  }

  // Detail Tabs
  .detail-tabs {
    display: flex;
    border-bottom: 2px solid $border-color;
    gap: 20px;
    margin-bottom: 20px;

    .detail-tab-btn {
      background: transparent;
      border: none;
      color: $text-muted;
      font-size: 14px;
      font-weight: 600;
      padding: 10px 0;
      cursor: pointer;
      position: relative;
      transition: color 0.2s;

      &:hover {
        color: $text-main;
      }

      &.active {
        color: $secondary;

        &::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 2px;
          background: $secondary;
        }
      }
    }
  }

  // Parameter Tables
  .table-container {
    background: $bg-card;
    border: 1px solid $border-color;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 20px;
  }

  .api-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th, td {
      padding: 12px 16px;
      text-align: left;
      border-bottom: 1px solid $border-color;
    }

    th {
      background: rgba(0, 0, 0, 0.2);
      font-weight: 600;
      color: $text-muted;
      font-size: 12px;
      text-transform: uppercase;
    }

    td {
      code {
        font-family: 'Fira Code', monospace;
        color: #e74c3c;
        background: rgba(231, 76, 60, 0.05);
        padding: 2px 6px;
        border-radius: 4px;
      }
    }
  }

  .empty-text {
    font-size: 13px;
    color: $text-muted;
    font-style: italic;
  }

  // Code Display Panels
  .code-block-wrapper {
    background: #0d1117;
    border: 1px solid $border-color;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 20px;

    .code-block-header {
      background: #161b22;
      padding: 8px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);

      .code-lang {
        font-size: 10px;
        text-transform: uppercase;
        font-weight: 700;
        color: $text-muted;
      }
    }

    .code-pre {
      margin: 0;
      padding: 16px;
      overflow-x: auto;
      font-family: 'Fira Code', monospace;
      font-size: 13px;
      line-height: 1.5;
      color: #c9d1d9;
    }
  }

  .response-sample-item {
    margin-bottom: 24px;

    .response-sample-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .sample-name {
        font-weight: 600;
        font-size: 13px;
      }
    }
  }

  .status-badge {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 4px;

    &.status-ok { background: rgba($primary, 0.15); color: $primary; }
    &.status-error { background: rgba($danger, 0.15); color: $danger; }
  }

  // Sandbox Panel (Right Side)
  .sandbox-panel {
    width: 380px;
    border-left: 1px solid $border-color;
    background-color: rgba($bg-card, 0.6);
    display: flex;
    flex-direction: column;
    flex-shrink: 0;

    .sandbox-header {
      padding: 16px;
      border-bottom: 1px solid $border-color;
      display: flex;
      justify-content: space-between;
      align-items: center;

      h3 {
        margin: 0;
        font-size: 14px;
        font-weight: 700;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .reset-btn {
        background: transparent;
        border: none;
        color: $text-muted;
        font-size: 11px;
        cursor: pointer;

        &:hover {
          color: $text-main;
        }
      }
    }

    .sandbox-fields {
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      border-bottom: 1px solid $border-color;
      overflow-y: auto;
      max-height: 50%;

      .sandbox-field-group {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .sandbox-label {
          font-size: 11px;
          font-weight: 600;
          color: $text-muted;
        }

        .url-input-row {
          display: flex;
          gap: 6px;

          .sandbox-select {
            background: $bg-input;
            border: 1px solid $border-color;
            color: $text-main;
            padding: 8px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 700;
          }

          .url-input {
            flex: 1;
          }
        }

        .sandbox-input, .sandbox-textarea {
          background: $bg-input;
          border: 1px solid $border-color;
          color: $text-main;
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 12px;
          width: 100%;

          &:focus {
            outline: none;
            border-color: $secondary;
          }
        }

        .sandbox-textarea {
          font-family: 'Fira Code', monospace;
          resize: vertical;
          height: 60px;

          &.body-textarea {
            height: 90px;
          }
        }
      }

      .send-request-btn {
        background: $secondary;
        color: white;
        border: none;
        padding: 10px;
        border-radius: 6px;
        font-weight: 700;
        font-size: 13px;
        cursor: pointer;
        transition: opacity 0.2s;

        &:hover {
          opacity: 0.9;
        }

        &:disabled {
          background: $border-color;
          cursor: not-allowed;
        }
      }
    }

    .sandbox-output-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .output-header {
        padding: 12px 16px;
        background: rgba(0, 0, 0, 0.1);
        border-bottom: 1px solid $border-color;
        display: flex;
        justify-content: space-between;
        align-items: center;

        h4 {
          margin: 0;
          font-size: 12px;
          font-weight: 700;
          color: $text-muted;
        }
      }

      .output-screen {
        flex: 1;
        background: #090d16;
        overflow-y: auto;
        padding: 16px;

        .placeholder-screen {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: $text-muted;
          font-size: 12px;
          font-style: italic;
          text-align: center;
        }

        .loading-screen {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: $text-muted;
          gap: 12px;
          font-size: 12px;
        }

        .response-display {
          display: flex;
          flex-direction: column;
          gap: 12px;

          .response-meta {
            display: flex;
            gap: 12px;
            font-size: 10px;
            color: $text-muted;
          }
        }
      }
    }
  }
}
</style>

<!-- JSON highlighting classes inside scoped styling -->
<style lang="scss">
.hl-key { color: #3b82f6; font-weight: 500; }
.hl-string { color: #10b981; }
.hl-number { color: #f59e0b; }
.hl-boolean { color: #9b59b6; }
.hl-null { color: #7f8c8d; }
</style>
